import { useCallback, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { ChangeSet, EntityRef, ModuleDiff, Status } from "@archlang/engine";
import {
  buildDiffColoring,
  ColorLegend,
  DiffNav,
  DiffDetailsPanel,
  // The ONE diff render substrate — Studio used to carry a fork of these
  // boards, and every substrate fix (single minimap, shared viewport store,
  // hover mirror) had to land twice or silently didn't. Wire-format modules
  // are structurally the scene arch-model shape the boards consume.
  SplitDiffBoard,
  OverlayDiffBoard,
} from "@archlang/app-shell/board";
import "@archlang/app-shell/index.css";
import { SegmentedControl } from "@archlang/ui";
import { ResizeHandle, RESIZE_HANDLE_SIZE } from "../ui/ResizeHandle.js";
import type { Module, DependencyEdge, StableId } from "./types.js";
import { useTranslation } from "../i18n";

type Mode = "split" | "overlay";

export interface StudioDiffBoardProps {
  readonly beforeMods: readonly Module[];
  readonly afterMods:  readonly Module[];
  readonly beforeEdges: readonly DependencyEdge[];
  readonly afterEdges:  readonly DependencyEdge[];
  readonly changeSet: ChangeSet;
  /** Used to fetch blame for the selected module in the after-side model. */
  readonly slug: string;
  readonly repo: string;
  /** The "to" ref (after side). */
  readonly toRef: string;
}

export function DiffBoard(props: StudioDiffBoardProps) {
  const { t } = useTranslation("diff");
  const [mode, setMode] = useState<Mode>("split");
  const [selectedRef, setSelectedRef] = useState<EntityRef | null>(null);
  // The board highlights modules only; derive a module id from the selection.
  const selectedId =
    selectedRef?.kind === "module"
      ? selectedRef.id
      : selectedRef?.kind === "interface"
        ? selectedRef.moduleId
        : null;
  const selectModule = (id: string | null) =>
    setSelectedRef(id ? ({ kind: "module", id } as EntityRef) : null);

  // Resizable side panels (Changes on the left, Details on the right).
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [changesW, setChangesW] = useState(280);
  const [detailsW, setDetailsW] = useState(320);
  const resizeChanges = useCallback((e: ReactPointerEvent) => {
    const rect = contentRef.current?.getBoundingClientRect();
    if (rect) setChangesW(Math.min(560, Math.max(180, e.clientX - rect.left)));
  }, []);
  const resizeDetails = useCallback((e: ReactPointerEvent) => {
    const rect = contentRef.current?.getBoundingClientRect();
    if (rect) setDetailsW(Math.min(600, Math.max(220, rect.right - e.clientX)));
  }, []);
  const stats = useMemo(() => changeStats(props.changeSet), [props.changeSet]);
  const total = stats.added + stats.removed + stats.modified + stats.renamed;

  // One unified diff colouring for both boards (stroke + minimap) and the
  // legend — built from the union of before+after modules.
  const coloring = useMemo(() => {
    const byId = new Map<StableId, Module>();
    for (const m of props.beforeMods) if (m.id) byId.set(m.id, m);
    for (const m of props.afterMods) if (m.id) byId.set(m.id, m); // after wins
    return buildDiffColoring([...byId.values()], props.changeSet);
  }, [props.beforeMods, props.afterMods, props.changeSet]);

  // Legend interaction: hover a status to preview its modules on the board,
  // click to pin. Effective highlight = hover (transient) over pin (sticky).
  const [legendHover, setLegendHover] = useState<string | null>(null);
  const [legendPinned, setLegendPinned] = useState<string | null>(null);
  const memberHighlightIds = useMemo(() => {
    const active = legendHover ?? legendPinned;
    return active ? coloring.legend.find((e) => e.id === active)?.memberIds ?? [] : [];
  }, [legendHover, legendPinned, coloring]);

  return (
    <div className="diff-page">
      <div className="diff-toolbar">
        <div className="diff-toolbar-group diff-toolbar-group--stats">
          <StatBadge kind="added"    count={stats.added}    />
          <StatBadge kind="modified" count={stats.modified} />
          <StatBadge kind="renamed"  count={stats.renamed}  />
          <StatBadge kind="removed"  count={stats.removed}  />
          {total === 0 && <span className="diff-no-changes">{t("board.noChanges")}</span>}
        </div>
        <div className="diff-toolbar-spacer" />
        <SegmentedControl
          label={t("board.viewMode")}
          active={mode}
          onSelect={setMode}
          items={[
            { id: "split", label: t("board.split") },
            { id: "overlay", label: t("board.overlay") },
          ]}
        />
      </div>
      <div
        className="diff-content"
        ref={contentRef}
        style={{
          gridTemplateColumns: `${changesW}px ${RESIZE_HANDLE_SIZE}px minmax(0,1fr) ${RESIZE_HANDLE_SIZE}px ${detailsW}px`,
        }}
      >
        <aside className="changes-panel">
          <DiffNav changeSet={props.changeSet} selected={selectedRef} onFocus={setSelectedRef} />
        </aside>
        <ResizeHandle orientation="vertical" ariaLabel={t("board.resizeChanges")} onResize={resizeChanges} />
        <div className="diff-boards">
          {mode === "split" ? (
            <SplitDiffBoard
              before={props.beforeMods}
              after={props.afterMods}
              beforeEdges={props.beforeEdges}
              afterEdges={props.afterEdges}
              changeSet={props.changeSet}
              coloring={coloring}
              memberHighlightIds={memberHighlightIds}
              selectedId={selectedId}
              onSelect={selectModule}
            />
          ) : (
            <OverlayDiffBoard
              before={props.beforeMods}
              after={props.afterMods}
              beforeEdges={props.beforeEdges}
              afterEdges={props.afterEdges}
              changeSet={props.changeSet}
              coloring={coloring}
              memberHighlightIds={memberHighlightIds}
              selectedId={selectedId}
              onSelect={selectModule}
            />
          )}
          {/* Status colour key — same NodeColoring the boards stroke with.
              Hover/click a row to highlight that status's modules. */}
          <ColorLegend
            title={coloring.title}
            entries={coloring.legend}
            pinnedId={legendPinned}
            onHover={setLegendHover}
            onPin={(id) => setLegendPinned((v) => (v === id ? null : id))}
          />
        </div>
        <ResizeHandle orientation="vertical" ariaLabel={t("board.resizeDetails")} onResize={resizeDetails} />
        <DiffDetailsPanel
          selected={selectedRef}
          before={props.beforeMods}
          after={props.afterMods}
          changeSet={props.changeSet}
        />
      </div>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

interface ChangeStats {
  added: number;
  removed: number;
  modified: number;
  renamed: number;
}

function changeStats(cs: ChangeSet): ChangeStats {
  const out: ChangeStats = { added: 0, removed: 0, modified: 0, renamed: 0 };
  const bump = (s: Status) => {
    if (s === "added") out.added++;
    else if (s === "removed") out.removed++;
    else if (s === "modified") out.modified++;
    else if (s === "renamed") out.renamed++;
  };
  const walk = (m: ModuleDiff) => {
    bump(m.status);
    for (const i of m.interfaces.values()) bump(i.status);
    for (const c of m.children.values()) walk(c);
  };
  for (const m of cs.modules.values()) walk(m);
  for (const p of cs.processes.values()) bump(p.status);
  for (const sp of cs.subprocesses.values()) bump(sp.status);
  for (const v of cs.views.values()) bump(v.status);
  return out;
}

const STAT_TITLE_KEY: Record<Status, string> = {
  added: "board.statAdded",
  removed: "board.statRemoved",
  modified: "board.statModified",
  renamed: "board.statRenamed",
  unchanged: "board.statModified",
};

function StatBadge({
  count,
  kind,
}: {
  readonly count: number;
  readonly kind: Status;
}) {
  const { t } = useTranslation("diff");
  if (count === 0) return null;
  const sigil =
    kind === "added" ? "+" :
    kind === "removed" ? "−" :
    kind === "modified" ? "~" : "↻";
  return (
    <span className={`diff-stat diff-stat--${kind}`} title={t(STAT_TITLE_KEY[kind], { count })}>
      <span className="diff-stat-sigil">{sigil}</span>
      <span className="diff-stat-count">{count}</span>
    </span>
  );
}
